import { useMemo } from 'react'

/**
 * Props:
 * - pageMetadata: { currentPage: number, limitValue: number, totalCount?: number }
 * - onPageChange: (page: number) => void
 * - pageRange: number of pages in the window (default: 5)
 * - classNames (optional): override Bootstrap classes
 * - labels (optional): override button texts
 */
function Pagination({
  pageMetadata = { currentPage: 1, limitValue: 10, totalCount: 0 },
  onPageChange,
  pageRange = 5,
  classNames = {
    ul: 'pagination mt-5',
    li: 'page-item',
    a: 'page-link'
  },
  labels = {
    first: '&laquo;', // «
    last: '&raquo;', // »
    prev: '&lsaquo; Prev', // ‹ Prev
    next: 'Next &rsaquo;' // Next ›
  }
}) {
  const { currentPage = 1, limitValue = 10, totalCount = 30 } = pageMetadata
  const totalPages = Math.max(1, Math.ceil((Number(totalCount) || 0) / Math.max(1, Number(limitValue) || 1)))

  // Normalize the current page within allowed boundaries
  const page = Math.min(Math.max(1, Number(currentPage) || 1), totalPages)

  const pages = useMemo(() => {
    const range = Math.max(1, Number(pageRange) || 1)
    if (totalPages <= range + 2) {
      // few pages – show all
      return Array.from({ length: totalPages }, (_, zeroBasedIndex) => zeroBasedIndex + 1)
    }

    const half = Math.floor(range / 2)
    let start = Math.max(1, page - half)
    let end = start + range - 1

    if (end > totalPages) {
      end = totalPages
      start = Math.max(1, end - range + 1)
    }

    const res = []
    // Always show the first page
    if (start > 1) {
      res.push(1)
      if (start > 2) res.push('ellipsis-left')
    }

    for (let pageNum = start; pageNum <= end; pageNum += 1) res.push(pageNum)

    // Always show the last page
    if (end < totalPages) {
      if (end < totalPages - 1) res.push('ellipsis-right')
      res.push(totalPages)
    }

    return res
  }, [page, pageRange, totalPages])

  const handleChange = (next) => {
    if (next !== page && next >= 1 && next <= totalPages) onPageChange?.(next)
  }

  const { ul, li, a } = classNames
  const { first, prev, next, last } = labels

  return (
    <nav aria-label='Pagination'>
      <ul className={ul}>
        {/* First */}
        <li className={`${li} ${page === 1 ? 'disabled' : ''}`}>
          <button
            type='button'
            className={a}
            aria-label='First page'
            disabled={page === 1}
            onClick={() => handleChange(1)}
          >
            <span dangerouslySetInnerHTML={{ __html: first }} />
          </button>
        </li>

        {/* Prev */}
        <li className={`${li} ${page === 1 ? 'disabled' : ''}`}>
          <button
            type='button'
            className={a}
            aria-label='Previous page'
            disabled={page === 1}
            onClick={() => handleChange(page - 1)}
          >
            <span dangerouslySetInnerHTML={{ __html: prev }} />
          </button>
        </li>

        {/* Numbered pages */}
        {pages.map((pageOrEllipsis, index) => {
          if (typeof pageOrEllipsis === 'string' && pageOrEllipsis.startsWith('ellipsis')) {
            return (
              <li key={`${pageOrEllipsis}-${index}`} className={`${li} disabled`} aria-hidden>
                <span className={a}>…</span>
              </li>
            )
          }
          const isActive = pageOrEllipsis === page
          return (
            <li key={pageOrEllipsis} className={`${li} ${isActive ? 'active' : ''}`} aria-current={isActive ? 'page' : undefined}>
              <button
                type='button'
                className={a}
                onClick={() => handleChange(pageOrEllipsis)}
                aria-label={`Go to page ${pageOrEllipsis}`}
              >
                {pageOrEllipsis}
              </button>
            </li>
          )
        })}

        {/* Next */}
        <li className={`${li} ${page === totalPages ? 'disabled' : ''}`}>
          <button
            type='button'
            className={a}
            aria-label='Next page'
            disabled={page === totalPages}
            onClick={() => handleChange(page + 1)}
          >
            <span dangerouslySetInnerHTML={{ __html: next }} />
          </button>
        </li>

        {/* Last */}
        <li className={`${li} ${page === totalPages ? 'disabled' : ''}`}>
          <button
            type='button'
            className={a}
            aria-label='Last page'
            disabled={page === totalPages}
            onClick={() => handleChange(totalPages)}
          >
            <span dangerouslySetInnerHTML={{ __html: last }} />
          </button>
        </li>
      </ul>
    </nav>
  )
}

export default Pagination
